import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import './MultiApartmentTimeline.css';
import { Apartment } from '../../api.apartments';
import { CustomPrice, ReservationSummary } from '../../api.pricingCalendar';
import { getToken } from '../../auth';
import { addDays, diffDays, fmtShort, MONTHS_CS, DOW_SHORT_CS, todayStr } from './dateUtils';
import { PAST_RESERVATION_COLORS, platformColors } from './reservationVisuals';
import { parseDowPrices, resolveDayPrice } from './pricing';
import { useMultiApartmentCalendarData } from './useMultiApartmentCalendarData';
import { DIRECT_BOOKING_RELEASED } from '../../featureFlags';

const WINDOW_DAYS = 31;
const WINDOW_STEP = 7;

// Single source of truth for both the CSS grid's cell sizes AND the reservation-chip pixel
// arithmetic below — they read from the same object, so they cannot disagree with each other.
const SIZES = {
  desktop: { col: 58, row: 40, sidebar: 185, monthRowH: 22, dowRowH: 16, dayRowH: 22, pad: 4 },
  mobile:  { col: 44, row: 46, sidebar: 84,  monthRowH: 18, dowRowH: 13, dayRowH: 18, pad: 4 },
};

function isWeekend(dateStr: string): boolean {
  const dow = new Date(dateStr + 'T00:00:00').getDay();
  return dow === 0 || dow === 6;
}

export interface MultiApartmentTimelineProps {
  apartments: Apartment[];
  rangeAptId: number | null;
  rangeStart: string | null;
  rangeEnd: string | null;
  onDayClick: (apartmentId: number, dateStr: string, customOverride?: CustomPrice) => void;
  onReservationClick: (apartmentId: number, res: ReservationSummary, dateStr: string) => void;
}

export interface MultiApartmentTimelineHandle {
  refreshApartment: (apartmentId: number) => Promise<void>;
}

const MultiApartmentTimeline = forwardRef<MultiApartmentTimelineHandle, MultiApartmentTimelineProps>(
  ({ apartments, rangeAptId, rangeStart, rangeEnd, onDayClick, onReservationClick }, ref) => {
    const today = todayStr();

    // Same breakpoint PricingCalendar.css already uses — checked via matchMedia (not the
    // useIsMobile hook, which uses a 768px threshold, one pixel off from this feature's 767px).
    const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches);
    useEffect(() => {
      const mq = window.matchMedia('(max-width: 767px)');
      const handler = () => setIsMobile(mq.matches);
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }, []);
    const sizes = isMobile ? SIZES.mobile : SIZES.desktop;

    const [windowStart, setWindowStart] = useState(() => addDays(today, -1));

    const days = useMemo(() => {
      const out: string[] = [];
      let d = windowStart;
      for (let i = 0; i < WINDOW_DAYS; i++) { out.push(d); d = addDays(d, 1); }
      return out;
    }, [windowStart]);
    const windowEnd = days[days.length - 1];

    // Group consecutive same-(year,month) entries for the month header row — derived from the
    // real dates in `days`, never hand-counted (see design spec §6: this exact bug class was
    // hit and fixed during design review).
    const monthGroups = useMemo(() => {
      const groups: { key: string; label: string; span: number }[] = [];
      for (const d of days) {
        const dt = new Date(d + 'T00:00:00');
        const key = `${dt.getFullYear()}-${dt.getMonth()}`;
        const last = groups[groups.length - 1];
        if (last && last.key === key) last.span += 1;
        else groups.push({ key, label: `${MONTHS_CS[dt.getMonth()]} ${dt.getFullYear()}`, span: 1 });
      }
      return groups;
    }, [days]);

    const validApartments = useMemo(() => apartments.filter(a => a.id != null), [apartments]);
    const apartmentIds = useMemo(() => validApartments.map(a => a.id as number), [validApartments]);

    const { dataByApartmentId, refreshApartment } = useMultiApartmentCalendarData(
      apartmentIds, windowStart, windowEnd, getToken()
    );
    useImperativeHandle(ref, () => ({ refreshApartment }), [refreshApartment]);

    const [rLo, rHi] = useMemo(() => {
      if (!rangeStart || !rangeEnd) return [null, null] as const;
      return rangeStart <= rangeEnd ? [rangeStart, rangeEnd] as const : [rangeEnd, rangeStart] as const;
    }, [rangeStart, rangeEnd]);

    const cssVars = {
      '--mat-col-w': `${sizes.col}px`,
      '--mat-row-h': `${sizes.row}px`,
      '--mat-sidebar-w': `${sizes.sidebar}px`,
      '--mat-month-h': `${sizes.monthRowH}px`,
      '--mat-dow-h': `${sizes.dowRowH}px`,
      '--mat-day-h': `${sizes.dayRowH}px`,
    } as React.CSSProperties;

    const headerH = sizes.monthRowH + sizes.dowRowH + sizes.dayRowH;

    // Reservation chips + the today-line are absolutely positioned within the grid container
    // using plain arithmetic against the fixed, known column width — no DOM measurement or
    // ResizeObserver needed here (unlike the single-apartment month grid, whose rows can wrap
    // at varying widths). Clips a reservation's edges to the visible window when it starts or
    // ends outside it, since the backend returns unclipped checkIn/checkOut. The shape fits
    // exactly to the occupied nights — flush on both ends, no fractional midday offset (that's
    // a single-apartment-grid-only convention; deliberately not reused here).
    const chips = useMemo(() => {
      const out: React.ReactNode[] = [];
      validApartments.forEach((apt, rowIndex) => {
        const calData = apt.id != null ? dataByApartmentId[apt.id] : null;
        if (!calData) return;
        for (const res of calData.reservations) {
          if (res.checkOut <= windowStart || res.checkIn >= addDays(windowEnd, 1)) continue;
          const hasCheckIn = res.checkIn >= windowStart;
          const hasCheckOut = res.checkOut <= addDays(windowEnd, 1);
          const clippedStart = hasCheckIn ? res.checkIn : windowStart;
          const clippedEnd = hasCheckOut ? res.checkOut : addDays(windowEnd, 1);
          const startIdx = diffDays(windowStart, clippedStart);
          const endIdx = diffDays(windowStart, clippedEnd);
          if (endIdx <= startIdx) continue;

          const left = sizes.sidebar + startIdx * sizes.col;
          const width = (endIdx - startIdx) * sizes.col;
          if (width <= 0) continue;

          const isPast = res.checkOut < today;
          const col = isPast ? PAST_RESERVATION_COLORS : platformColors(res.platform);
          const radius = 6;
          const tl = hasCheckIn ? radius : 0;
          const tr = hasCheckOut ? radius : 0;
          const guests = res.numberOfGuests ? ` · ${res.numberOfGuests} os.` : '';

          out.push(
            <div
              key={`${apt.id}-${res.id}`}
              className="mat-chip"
              style={{
                top: headerH + rowIndex * sizes.row + sizes.pad,
                left,
                width,
                height: sizes.row - sizes.pad * 2,
                background: col.bg,
                borderTop: `2px solid ${col.border}`,
                borderBottom: `2px solid ${col.border}`,
                borderLeft: hasCheckIn ? `2px solid ${col.border}` : 'none',
                borderRight: hasCheckOut ? `2px solid ${col.border}` : 'none',
                borderRadius: `${tl}px ${tr}px ${tr}px ${tl}px`,
                color: col.text,
              }}
              onClick={() => apt.id != null && onReservationClick(apt.id, res, clippedStart)}
            >
              {res.guestName || '—'}{guests}
            </div>
          );
        }
      });
      return out;
    }, [validApartments, dataByApartmentId, windowStart, windowEnd, sizes, headerH, today, onReservationClick]);

    const todayIdx = days.indexOf(today);

    return (
      <div className="mat-wrap">
        <div className="mat-nav">
          <button className="mat-navbtn mat-navbtn-today" onClick={() => setWindowStart(addDays(today, -1))}>Dnes</button>
          <button className="mat-navbtn" onClick={() => setWindowStart(s => addDays(s, -WINDOW_STEP))}>‹</button>
          <div className="mat-navlabel">{fmtShort(windowStart)} – {fmtShort(windowEnd)}</div>
          <button className="mat-navbtn" onClick={() => setWindowStart(s => addDays(s, WINDOW_STEP))}>›</button>
        </div>

        <div className="mat-board" style={cssVars}>
          <div className="mat-scroll">
            <div className="mat-inner" style={{ gridTemplateColumns: `var(--mat-sidebar-w) repeat(${WINDOW_DAYS}, var(--mat-col-w))` }}>

              <div className="mat-corner">Apartmán</div>

              {monthGroups.map(g => (
                <div key={g.key} className="mat-monthlabel" style={{ gridColumn: `span ${g.span}` }}>{g.label}</div>
              ))}

              {days.map(d => {
                const weekend = isWeekend(d);
                const isPast = d < today;
                const isToday = d === today;
                const cls = `mat-dow${weekend ? ' mat-weekend' : ''}${isPast ? ' mat-past' : ''}${isToday ? ' mat-today' : ''}`;
                return (
                  <div key={d} className={cls} style={{ gridRow: 2 }}>
                    {DOW_SHORT_CS[(new Date(d + 'T00:00:00').getDay() + 6) % 7]}
                  </div>
                );
              })}

              {days.map(d => {
                const weekend = isWeekend(d);
                const isPast = d < today;
                const isToday = d === today;
                const cls = `mat-daynum${weekend ? ' mat-weekend' : ''}${isPast ? ' mat-past' : ''}${isToday ? ' mat-today' : ''}`;
                return (
                  <div key={d} className={cls} style={{ gridRow: 3 }}>
                    {isToday ? 'Dnes' : new Date(d + 'T00:00:00').getDate()}
                  </div>
                );
              })}

              {!isMobile && todayIdx >= 0 && (
                <div
                  className="mat-today-line"
                  style={{ top: headerH, left: sizes.sidebar + todayIdx * sizes.col }}
                />
              )}

              {validApartments.map((apt, rowIndex) => {
                const calData = apt.id != null ? dataByApartmentId[apt.id] : null;
                const noSite = calData != null && !calData.hasBookingSite;
                // Direct booking isn't released yet, so every apartment currently has no site —
                // showing the "missing" warning everywhere right now would just be noise. The
                // underlying `noSite` still gates real functional behavior below (click-to-edit
                // disabled where there's genuinely no pricing to edit); only the visual warning
                // cue is suppressed. See featureFlags.ts.
                const showNoSiteWarning = DIRECT_BOOKING_RELEASED && noSite;
                const dowPrices = parseDowPrices(calData?.raisedPriceJson);
                const customPriceMap = Object.fromEntries((calData?.customPrices ?? []).map(p => [p.date, p]));

                return (
                  <React.Fragment key={apt.id}>
                    <div className={`mat-aptcell${showNoSiteWarning ? ' mat-nosite' : ''}`}>
                      <span className="mat-aptname">{apt.internalName || apt.name}</span>
                      {showNoSiteWarning && (
                        <span className="mat-warn-icon" title="Bez ceníku — chybí přímá rezervace">⚠</span>
                      )}
                    </div>

                    {days.map(d => {
                      const res = calData?.reservations.find(r => r.checkIn <= d && d < r.checkOut) ?? null;
                      const isPast = d < today;
                      const isRangeAnchor = rangeAptId === apt.id && !!rangeStart && !rangeEnd && d === rangeStart;
                      const isInRange = rangeAptId === apt.id && !!rLo && !!rHi && d >= rLo && d <= rHi;
                      const { price, promo, minNights, isBlocked } = resolveDayPrice(customPriceMap, dowPrices, calData?.basePricePerNight, d);

                      let onClick: (() => void) | undefined;
                      if (apt.id != null) {
                        if (res) onClick = () => onReservationClick(apt.id as number, res, d);
                        else if (calData?.hasBookingSite && d >= today) onClick = () => onDayClick(apt.id as number, d, customPriceMap[d]);
                      }

                      const cls = ['mat-cell'];
                      if (isPast) cls.push('mat-past');
                      if (showNoSiteWarning) cls.push('mat-nosite');
                      if (isBlocked) cls.push('mat-blocked');
                      if (isRangeAnchor || isInRange) cls.push('mat-in-range');
                      if (!onClick) cls.push('mat-no-click');

                      if (res || noSite) {
                        return <div key={d} className={cls.join(' ')} onClick={onClick} />;
                      }

                      if (isBlocked) {
                        return (
                          <div key={d} className={cls.join(' ')} onClick={onClick}>
                            <span className="mat-blocked-icon">🚫</span>
                          </div>
                        );
                      }

                      return (
                        <div key={d} className={cls.join(' ')} onClick={onClick}>
                          {price != null && (
                            promo != null && promo < price
                              ? <span className="mat-price mat-price-promo">{promo}</span>
                              : <span className="mat-price">{price}</span>
                          )}
                          {minNights != null && minNights > 1 && <span className="mat-minnights">🌙 {minNights}</span>}
                        </div>
                      );
                    })}
                  </React.Fragment>
                );
              })}

              {chips}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

MultiApartmentTimeline.displayName = 'MultiApartmentTimeline';

export default MultiApartmentTimeline;
